import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";
import api, { apiErr } from "./lib/api";
import Header from "./components/Header";
import BottomNav from "./components/BottomNav";
import Footer from "./components/Footer";
import OpenPackFlow from "./components/OpenPackFlow";
import SbustaScreen from "./screens/SbustaScreen";
import CollezioneScreen from "./screens/CollezioneScreen";
import BonusScreen from "./screens/BonusScreen";
import ShareView from "./screens/ShareView";

function MainApp() {
  const [config, setConfig] = useState(null);
  const [state, setState] = useState(null);
  const [sets, setSets] = useState([]);
  const [tab, setTab] = useState("sbusta");
  const [flowSet, setFlowSet] = useState(null); // set currently being opened (gesture flow)
  const [recoverKey, setRecoverKey] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadSets = useCallback(() => {
    api.get("/sets").then((r) => setSets(r.data.sets)).catch(() => {});
  }, []);

  const refreshState = useCallback(() => {
    api.get("/me").then((r) => setState(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    (async () => {
      try {
        const { data } = await api.post("/session/init", { ref: ref || null });
        setState(data);
        if (ref) window.history.replaceState({}, "", window.location.pathname);
      } catch (e) {
        /* ignore */
      }
      api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
      loadSets();
    })();
  }, [loadSets]);

  const imagesEnabled = config?.images_enabled !== false;

  // enter the gesture flow (no server call yet)
  const doOpen = useCallback((setObj) => {
    setRecoverKey(null);
    setFlowSet(setObj);
  }, []);

  // server confirmed an opening (from inside the flow)
  const onOpened = useCallback(
    (data) => {
      setState(data.state);
      loadSets();
      setRefreshKey((k) => k + 1);
      if (data.achievements?.length) {
        data.achievements.forEach((a) => toast.success(`Obiettivo! ${a.milestone} bustine · +${a.reward} bonus`));
      }
    },
    [loadSets]
  );

  // recover an interrupted opening after a page refresh
  useEffect(() => {
    if (!sets.length || flowSet) return;
    let pending = null;
    try {
      pending = JSON.parse(localStorage.getItem("luma_pending_open") || "null");
    } catch (e) { /* ignore */ }
    if (pending?.key && pending?.set_id) {
      const s = sets.find((x) => x.set_id === pending.set_id);
      if (s) {
        setRecoverKey(pending.key);
        setFlowSet(s);
      } else {
        localStorage.removeItem("luma_pending_open");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sets]);

  const doClaim = useCallback(async () => {
    try {
      const { data } = await api.post("/daily-claim");
      setState(data.state);
      if (data.already_claimed) {
        toast("Bonus già riscattato oggi. Torna domani!");
      } else {
        toast.success("Bonus riscattato! +1 bustina bonus");
        if (data.bonus_collection_card) {
          toast.success(`Carta collezione bonus: ${data.bonus_collection_card.name} (${data.bonus_collection_card.number})`);
        }
      }
      loadSets();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(apiErr(e));
    }
  }, [loadSets]);

  return (
    <div className="min-h-screen pb-24">
      <Header state={state} onTick={refreshState} />

      <main className="max-w-md sm:max-w-2xl lg:max-w-5xl mx-auto">
        {tab === "sbusta" && (
          <SbustaScreen
            sets={sets}
            state={state}
            onOpen={doOpen}
            onGoBonus={() => setTab("bonus")}
            dailyClaimedToday={state?.daily_claimed_today}
          />
        )}
        {tab === "collezione" && (
          <CollezioneScreen sets={sets} imagesEnabled={imagesEnabled} refreshKey={refreshKey} />
        )}
        {tab === "bonus" && (
          <BonusScreen state={state} onClaim={doClaim} refreshState={refreshState} />
        )}

        <Footer config={config} />
      </main>

      <BottomNav active={tab} onChange={setTab} bonusDot={state && !state.daily_claimed_today} />

      {flowSet && (
        <OpenPackFlow
          key={flowSet.set_id + (recoverKey || "")}
          set={flowSet}
          state={state}
          imagesEnabled={imagesEnabled}
          recoverKey={recoverKey}
          onOpened={onOpened}
          onClose={() => {
            setFlowSet(null);
            setRecoverKey(null);
          }}
        />
      )}

      <Toaster position="top-center" richColors toastOptions={{ style: { fontFamily: "Inter, sans-serif", fontWeight: 600 } }} />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/share/:token" element={<ShareView />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}
